import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import * as yup from "yup";
import Drawer from "../../assets/vfx/drawer/drawer";
import Form from "../../assets/vfx/form/form";
import { Constants } from "../../assets/utils/lib/constants";
import { LookupDto, ServiceRequestLookupsDto, ServiceRequestDto, AttachmentFileDto } from "../../services/dto";
import { ClaimHelper, FileHelper } from "../../assets/utils/helpers";
import { useCommunity } from "../../context/communityContext";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../assets/utils/lib";

interface ServiceRequestFormProps {
    show: boolean;
    isLoading?: boolean;
    lookups?: ServiceRequestLookupsDto;
    isLoadingLookups?: boolean;
    onHide: () => void;
    onSave: (payload: ServiceRequestDto) => DrawerFormSaveResult;
}

interface ServiceRequestFormFieldsProps {
    lookups?: ServiceRequestLookupsDto;
    isLoading?: boolean;
    selectedFiles: File[];
    communityId?: string | null;
    blankOption: (display: string) => { key: string; value: string; display: string };
    onFilesChange: (files: File[]) => void;
}

const blankOption = (display: string) => ({
    key: Constants.emptyGuid,
    value: Constants.emptyString,
    display,
});

const ACCEPTED_FILE_TYPES = [
    "image/jpeg", "image/png", "image/gif", "image/webp",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
].join(",");

const getDecimalScale = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 0;
    const str = num.toString();
    if (str.includes(".")) {
        return str.split(".")[1].length;
    }
    return 0;
};

const validationSchema = yup.object().shape({
    communityId: yup.string().required("Community is required."),
    requestTypeId: yup.string().required("Request Type is required."),
    actionTypeId: yup.string().required("Action Type is required."),
    statusId: yup.string().required("Status is required."),
    priority: yup.string().required("Priority is required."),
    description: yup.string().required("Description is required."),
    citizenName: yup.string().required("Citizen Name is required."),
    citizenEmail: yup.string().email("Invalid email.").nullable(),
    citizenPhone: yup.string().nullable(),
    addressLine1: yup.string().required("Address Line 1 is required."),
    addressLine2: yup.string().nullable(),
    city: yup.string().required("City is required."),
    stateId: yup.string().required("State is required."),
    zip: yup.string().required("Zip is required."),
    latitude: yup.number()
        .typeError("Latitude must be a number")
        .min(-90, "Latitude must be between -90 and 90.")
        .max(90, "Latitude must be between -90 and 90.")
        .test("scale", "Latitude must not have more than 6 decimal places.", (value) =>
            value === undefined || value === null || getDecimalScale(value) <= 6
        )
        .nullable(),
    longitude: yup.number()
        .typeError("Longitude must be a number")
        .min(-180, "Longitude must be between -180 and 180.")
        .max(180, "Longitude must be between -180 and 180.")
        .test("scale", "Longitude must not have more than 6 decimal places.", (value) =>
            value === undefined || value === null || getDecimalScale(value) <= 6
        )
        .nullable(),
    departmentId: yup.string().required("Department is required."),
});

const initialValues = {
    communityId: Constants.emptyString,
    requestTypeId: Constants.emptyString,
    actionTypeId: Constants.emptyString,
    statusId: Constants.emptyString,
    priority: Constants.emptyString,
    description: Constants.emptyString,
    citizenName: Constants.emptyString,
    citizenEmail: null as string | null,
    citizenPhone: null as string | null,
    addressLine1: Constants.emptyString,
    addressLine2: null as string | null,
    city: Constants.emptyString,
    stateId: Constants.emptyString,
    zip: Constants.emptyString,
    latitude: null as number | null,
    longitude: null as number | null,
    departmentId: Constants.emptyString,
    attachments: [] as File[],
};

export const ServiceRequestForm: React.FC<ServiceRequestFormProps> = (props) => {
    const isLoading = props.isLoading || props.isLoadingLookups;
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const claimCommunityId = ClaimHelper.getCommunityId();
    const { communityId } = useCommunity();
    const resolvedCommunityId = claimCommunityId || communityId;

    useEffect(() => {
        if (props.show) {
            setSelectedFiles([]);
        }
    }, [props.show]);

    const handleFormSubmit = async (values: typeof initialValues) => {
        const attachments: AttachmentFileDto[] = [];

        if (selectedFiles.length > 0) {
            for (const file of selectedFiles) {
                try {
                    const fileDto: AttachmentFileDto = await FileHelper.FileToImageFileDto(file);
                    attachments.push(fileDto);
                } catch (error) {
                    console.error("Error converting", error);
                }
            }
        }

        const dto: ServiceRequestDto = {
            id: Constants.emptyGuid,
            communityId: values.communityId,
            requestTypeId: values.requestTypeId,
            statusId: values.statusId,
            priorityId: values.priority,
            description: values.description,
            citizenName: values.citizenName,
            citizenEmail: values.citizenEmail || undefined,
            citizenPhone: values.citizenPhone || undefined,
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2 || undefined,
            city: values.city,
            stateId: values.stateId,
            zip: values.zip,
            latitude: values.latitude || undefined,
            longitude: values.longitude || undefined,
            departmentId: values.departmentId,
            submittedById: ClaimHelper.getUserId(),
            attachments: attachments.length > 0 ? attachments : undefined,
            requestAction: {
                id: Constants.emptyGuid,
                requestId: Constants.emptyGuid,
                actionDate: new Date(),
                actionTypeId: values.actionTypeId,
                comment: "Submitted via Admin Panel",
            },
        };
        props.onSave(dto);
    };

    return (
        <Drawer.Form
            show={props.show}
            onHide={props.onHide}
            title="New Service Request"
            initialValues={{ ...initialValues, communityId: resolvedCommunityId }}
            validationSchema={validationSchema}
            on_Submit={handleFormSubmit}
            placement="end"
            style={{
                width: "40%",
            }}
        >
            <ServiceRequestFormFields
                lookups={props.lookups}
                isLoading={isLoading}
                selectedFiles={selectedFiles}
                communityId={resolvedCommunityId}
                onFilesChange={setSelectedFiles}
                blankOption={blankOption}
            />
        </Drawer.Form>
    );
};

const ServiceRequestFormFields: React.FC<ServiceRequestFormFieldsProps> = (props) => {
    const fieldLabelWidth = "180px";

    return (
        <Row>
            <Col xs={12}>
                <Form.Dropdown
                    name="communityId"
                    label="Community"
                    labelWidth={fieldLabelWidth}
                    options={LookupDto.mapToOptions(props.lookups?.community)}
                    isLoading={props.isLoading}
                    blankOption={props.blankOption("Select Community")}
                    disabled={!!props.communityId}
                />
            </Col>

            <Col xs={12} sm={6}>
                <Form.Dropdown
                    name="requestTypeId"
                    label="Request Type"
                    labelWidth={fieldLabelWidth}
                    options={LookupDto.mapToOptions(props.lookups?.requestType)}
                    isLoading={props.isLoading}
                    blankOption={props.blankOption("Select Request Type")}
                />
            </Col>

            <Col xs={12} sm={6}>
                <Form.Dropdown
                    name="actionTypeId"
                    label="Action Type"
                    labelWidth={fieldLabelWidth}
                    options={LookupDto.mapToOptions(props.lookups?.actionType)}
                    isLoading={props.isLoading}
                    blankOption={props.blankOption("Select Action Type")}
                />
            </Col>

            <Col xs={12} sm={6}>
                <Form.Dropdown
                    name="statusId"
                    label="Status"
                    labelWidth={fieldLabelWidth}
                    options={LookupDto.mapToOptions(props.lookups?.status)}
                    isLoading={props.isLoading}
                    blankOption={props.blankOption("Select Status")}
                />
            </Col>

            <Col xs={12} sm={6}>
                <Form.Dropdown
                    name="priority"
                    label="Priority"
                    labelWidth={fieldLabelWidth}
                    options={LookupDto.mapToOptions(props.lookups?.priority)}
                    isLoading={props.isLoading}
                    blankOption={props.blankOption("Select Priority")}
                />
            </Col>

            <Col xs={12}>
                <Form.Dropdown
                    name="departmentId"
                    label="Department"
                    labelWidth={fieldLabelWidth}
                    options={LookupDto.mapToOptions(props.lookups?.department)}
                    isLoading={props.isLoading}
                    blankOption={props.blankOption("Select Department")}
                />
            </Col>

            <Col xs={12}>
                <Form.TextArea
                    name="description"
                    label="Description"
                    labelWidth={fieldLabelWidth}
                    rows={4}
                    placeholder="Provide details about the issue"
                    isLoading={props.isLoading}
                />
            </Col>

            <Col xs={12}>
                <Form.Text
                    name="citizenName"
                    label="Citizen Name"
                    labelWidth={fieldLabelWidth}
                    placeholder="Enter citizen name"
                    isLoading={props.isLoading}
                />
            </Col>

            <Col xs={12}>
                <Form.Group label="Contact" labelWidth={fieldLabelWidth}>
                    <Row>
                        <Col xs={12} sm={6}>
                            <Form.Text
                                name="citizenEmail"
                                placeholder="Enter citizen email"
                                isLoading={props.isLoading}
                            />
                        </Col>

                        <Col xs={12} sm={6}>
                            <Form.Phone
                                name="citizenPhone"
                                placeholder="Enter citizen phone"
                                isLoading={props.isLoading}
                            />
                        </Col>
                    </Row>
                </Form.Group>
            </Col>

            <Col xs={12}>
                <Form.Group label="Address" labelWidth={fieldLabelWidth}>
                    <Form.Text
                        name="addressLine1"
                        placeholder="Enter address"
                        isLoading={props.isLoading}
                    />

                    <Form.Text
                        name="addressLine2"
                        placeholder="Enter address line 2"
                        isLoading={props.isLoading}
                    />

                    <Row>
                        <Col xs={12} sm={6}>
                            <Form.Text
                                name="city"
                                placeholder="Enter city"
                                isLoading={props.isLoading}
                            />
                        </Col>

                        <Col xs={12} sm={6}>
                            <Form.Dropdown
                                name="stateId"
                                options={LookupDto.mapToOptions(props.lookups?.state)}
                                isLoading={props.isLoading}
                                blankOption={props.blankOption("Select State")}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} sm={4}>
                            <Form.Zip
                                name="zip"
                                placeholder="Enter zip"
                                isLoading={props.isLoading}
                            />
                        </Col>

                        <Col xs={12} sm={4}>
                            <Form.Number
                                name="latitude"
                                placeholder="Enter latitude"
                                isLoading={props.isLoading}
                            />
                        </Col>

                        <Col xs={12} sm={4}>
                            <Form.Number
                                name="longitude"
                                placeholder="Enter longitude"
                                isLoading={props.isLoading}
                            />
                        </Col>
                    </Row>
                </Form.Group>
            </Col>

            <Col xs={12}>
                <Form.Attachments
                    name="attachments"
                    label="Attachments"
                    labelWidth={fieldLabelWidth}
                    note="Allowed: Images, PDF, office documents"
                    selectedFiles={props.selectedFiles}
                    onFilesChange={props.onFilesChange}
                    accept={ACCEPTED_FILE_TYPES}
                    isLoading={props.isLoading}
                />
            </Col>
        </Row>
    );
};
